import { useEffect } from 'react';
import { useAuth } from '@/auth';
import { useAppStore } from '@/stores/appStore';
import { setRuntimeManifestContext, clearManifestCache } from '@/services/operationManifestService';

const isE2EMode = import.meta.env.VITE_E2E_MODE === 'true';
const isLocalMode = import.meta.env.VITE_LOCAL_MODE === 'true';

/**
 * Syncs the current app's auth context into the manifest service module,
 * enabling runtime manifest fetching for operations without static manifests.
 *
 * Discovery hints (icons, colors for fallback manifests) are populated lazily
 * by usePreloadOperations via useBuiltInOperationsQuery when the designer mounts,
 * rather than eagerly fetched here to avoid duplicate /operations calls.
 *
 * Disabled in E2E and local modes where the Logic Apps runtime is unavailable.
 * Call once in a high-level layout component (e.g., Layout).
 */
export function useRuntimeManifestSync(): void {
  const { getOttoToken } = useAuth();
  const { selectedProject, selectedApp } = useAppStore();

  useEffect(() => {
    // Skip in E2E/local mode — no real runtime to fetch from
    if (isE2EMode || isLocalMode) return;

    if (!selectedProject || !selectedApp) {
      clearManifestCache();
      setRuntimeManifestContext(null);
      return;
    }

    // Clear cached manifests from the previous app — runtime manifests are app-specific
    clearManifestCache();

    setRuntimeManifestContext({
      projectName: selectedProject,
      appName: selectedApp.name,
      getAccessToken: getOttoToken,
    });

    return () => {
      setRuntimeManifestContext(null);
    };
  }, [selectedApp, selectedProject, getOttoToken]);
}
